import React, { useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Pagination, Autoplay, EffectFade } from "swiper";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import CardSlide from "./CardSlide";
import ImageSlide from "./ImageSlide";
import SwiperArrows from "../Slider/SliderArrows";

const HomepageSlider = ({ slidesJson }) => {
	const slides = JSON.parse(slidesJson);
	const imageSliderRef = useRef(null);
	const cardSliderRef = useRef(null);
	const totalSlides = slides.length;

	// Simple navigation handlers to control both sliders
	const handleNext = useCallback(() => {
		if (!imageSliderRef.current || !cardSliderRef.current) return;
		if (imageSliderRef.current.animating || cardSliderRef.current.animating)
			return;

		const nextRealIndex = (imageSliderRef.current.realIndex + 1) % totalSlides;

		// Let the main slider advance naturally in loop mode
		// Sync the other slider using the logical index, preserving loop behavior
		imageSliderRef.current.slideNext();
		cardSliderRef.current.slideToLoop(nextRealIndex);

	}, [totalSlides]);

	const handlePrev = useCallback(() => {
		if (!imageSliderRef.current || !cardSliderRef.current) return;
		if (imageSliderRef.current.animating || cardSliderRef.current.animating)
			return;

		const prevRealIndex =
			(imageSliderRef.current.realIndex - 1 + totalSlides) % totalSlides;

		imageSliderRef.current.slidePrev();
		cardSliderRef.current.slideToLoop(prevRealIndex);
	}, [totalSlides]);

	// Sync sliders when one changes (drag/swipe)
	const handleImageSlideChange = useCallback((swiper) => {
		if (cardSliderRef.current) {
			cardSliderRef.current.slideToLoop(swiper.realIndex);
		}
	}, []);

	return (
		<div className="homepage-slider">
			<div className="homepage-slider__image-slide-container">
				<Swiper
					modules={[A11y, Autoplay]}
					slidesPerView={1}
					loop={true}
					speed={500}
					onSwiper={(swiper) => {
						imageSliderRef.current = swiper;
					}}
					autoplay={{ delay: 4000, disableOnInteraction: true }}
					onSlideChange={handleImageSlideChange}
					className="homepage-slider__image-slider"
				>
					{slides.map((slide, index) => {
						return (
							<SwiperSlide key={index}>
								<ImageSlide {...slide} />
							</SwiperSlide>
						);
					})}
				</Swiper>
			</div>
			<div className="homepage-slider__card-slide-container">
				<div className="card-container">
					<SwiperArrows onNext={handleNext} onPrev={handlePrev} />
					<Swiper
						pagination={{ clickable: false, el: ".swiper-pagination" }}
						modules={[A11y, Pagination, EffectFade]}
						effect="fade"
						fadeEffect={{
							crossFade: true,
						}}
						onSwiper={(swiper) => {
							cardSliderRef.current = swiper;
						}}
						allowTouchMove={false}
						slidesPerView={1}
						loop={true}
						autoplay={{ delay: 4000, disableOnInteraction: true }}
						className="homepage-slider__card-slider"
					>
						{slides.map((slide, index) => {
							return (
								<SwiperSlide key={index}>
									<CardSlide {...slide} />
								</SwiperSlide>
							);
						})}
					</Swiper>
					<div className="swiper-pagination" />
				</div>
			</div>
		</div>
	);
};

HomepageSlider.propTypes = {
	slidesJson: PropTypes.string,
};

export default HomepageSlider;
